/**
 * 颜色解析：支持 HEX（#RRGGBB）/ RGB（rgb(r,g,b)）
 * @param {string} input - 颜色值
 * @returns {{ r: number, g: number, b: number }} - RGB 分量
 */
const parseColor = (input: string): { r: number; g: number; b: number } => {
  let r = 0; let g = 0; let b = 0;
  if (input.startsWith('#')) { // 处理 HEX 格式（如 #ff0000）
    const hex = input.slice(1).padEnd(6, '0');
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else if (input.startsWith('rgb')) { // 处理 RGB 格式（如 rgb(255,0,0)）
    const match = input.match(/(\d+),\s*(\d+),\s*(\d+)/);
    if (match) [r, g, b] = match.slice(1, 4).map(Number);
  }
  return { r, g, b };
};

/**
 * 实现 Less 的 fade 函数
 * @param {string} color - 颜色值（支持 HEX/RGB/颜色关键字）
 * @param {number} amount - 透明度百分比 (0-100)
 * @returns {string} - 返回 RGBA 格式的颜色值
 */
export const fade = (color: string, amount: number): string => {
  // 计算新透明度 (0-1 范围)
  const alpha = Math.max(0, Math.min(1, amount / 100));
  const { r, g, b } = parseColor(color);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * 在颜色上叠加指定比例的黑色，返回不透明的 HEX 结果。
 * 等价于 Less 中 mixColor(color, black, ratio) 的效果（LiteApp 不支持该动画混色写法，改由 JS 预计算）。
 * @param {string} color - 原始颜色值（支持 HEX/RGB）
 * @param {number} ratio - 叠加黑色的比例 (0-1)，如 0.1 表示叠加 10% 黑色
 * @returns {string} - 返回 HEX 格式的颜色值
 */
export const darken = (color: string, ratio: number): string => {
  const k = Math.max(0, Math.min(1, ratio));
  const { r, g, b } = parseColor(color);
  const toHex = (c: number): string => Math.round(c * (1 - k)).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
